import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import { useActiveCycle } from "@src/packages/cycles";
import type { PartnerSharingScope } from "./usePartnerInvite";

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isUuid(value: string | null | undefined): value is string {
  return value !== null && value !== undefined && UUID_PATTERN.test(value);
}

/**
 * Resolves the active cycle's actual family-building-case link. A
 * development-only cycle override or an incomplete clinical graph never
 * becomes an API permission scope.
 */
export function usePartnerSharingScope(): {
  scope: PartnerSharingScope | null;
  isLoading: boolean;
  isAvailable: boolean;
} {
  const { client, patientId } = useApiClient();
  const activeCycleQuery = useActiveCycle();
  const cycleId = activeCycleQuery.data?.id;
  const cycleEntityQuery = useQuery({
    queryKey: ["clinical-entity-resolve", patientId, "cycle", cycleId],
    queryFn: async () => {
      const { data, error, response } = await client.GET(
        "/patients/{patientId}/clinical-entities/resolve",
        {
          params: {
            path: { patientId: patientId! },
            query: { kind: "cycle", recordId: cycleId! },
          },
        },
      );

      if (data) return data.data;
      throwApiError(
        error,
        "Failed to resolve active cycle",
        "usePartnerSharingScope",
        response?.status,
      );
    },
    enabled: !!patientId && isUuid(cycleId),
    staleTime: 30_000,
  });
  const cycleEntityId = cycleEntityQuery.data?.id;
  const cycleLinksQuery = useQuery({
    queryKey: ["clinical-entity-links", patientId, cycleEntityId],
    queryFn: async () => {
      const { data, error, response } = await client.GET(
        "/patients/{patientId}/clinical-entities/{entityId}/links",
        {
          params: {
            path: { patientId: patientId!, entityId: cycleEntityId! },
            query: { limit: 100 },
          },
        },
      );

      if (data) return data.data;
      throwApiError(
        error,
        "Failed to resolve active cycle links",
        "usePartnerSharingScope",
        response?.status,
      );
    },
    enabled: !!patientId && isUuid(cycleEntityId),
    staleTime: 30_000,
  });
  const caseLink = cycleLinksQuery.data?.items.find(
    (link) =>
      link.lifecycle === "active" &&
      link.fromEntityId === cycleEntityId &&
      link.fromKindCode === "cycle" &&
      link.toKindCode === "family_building_case",
  );
  const caseId = caseLink?.toEntityId;
  const scope = isUuid(caseId) && isUuid(cycleId) ? { caseId, cycleId } : null;

  return {
    scope,
    isLoading:
      activeCycleQuery.isLoading ||
      cycleEntityQuery.isLoading ||
      cycleLinksQuery.isLoading,
    isAvailable: scope !== null,
  };
}
